import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, ExternalLink, MapPin, Star, Utensils } from 'lucide-react';
import { getShoutout } from '../../services/shoutout';
import { formatCurrency } from '../../Utils';
import { usePlatformBrand } from '../../Context/PlatformBrand/PlatformBrandContext';

const DEFAULT_ACCENT = '#1A7F6D';

export default function ShoutoutPage() {
  const platformBrand = usePlatformBrand();
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getShoutout(token)
      .then((res) => {
        if (!active) return;
        if (!res?.success) throw new Error(res?.error || 'This Shout-Out is not available.');
        setData(res.data);
      })
      .catch((err) => {
        if (active) setError(err.message || 'This Shout-Out is not available.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  const restaurant = data?.restaurant;
  const customer = data?.customer;
  const shoutout = data?.shoutout;
  const highlightedItem = data?.highlightedItem;
  const popularItems = data?.popularItems || [];
  const accentColor = DEFAULT_ACCENT;
  const restaurantLogo = restaurant?.logo_url;
  const restaurantName = restaurant?.name || 'this local restaurant';
  const shoutoutMessage = shoutout?.public_message || 'This place is worth discovering.';
  const rating = Number(shoutout?.rating || 0);
  const firstName = useMemo(() => {
    const name = customer?.name || 'Someone local';
    return name.split(' ')[0] || name;
  }, [customer?.name]);

  const openMenu = () => {
    if (!restaurant?.id) return;
    const table = searchParams.get('table') || '0';
    const ref = customer?.referral_code || searchParams.get('ref') || '';
    const url = `/menu/${restaurant.id}/${encodeURIComponent(table)}/default?${new URLSearchParams({
      ...(ref ? { ref } : {}),
      shoutout: token,
    }).toString()}`;
    navigate(url);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f7f5] px-5">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-neutral-200 border-t-neutral-900" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f7f5] px-5 text-center">
        <div>
          <p className="text-base font-semibold text-neutral-950">Shout-Out unavailable</p>
          <p className="mt-2 text-sm text-neutral-500">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-5 rounded-xl bg-neutral-950 px-5 py-3 text-sm font-semibold text-white"
          >
            Scan a restaurant QR
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f4ef] text-neutral-950">
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-5 pb-8 pt-5">
        <div className="mb-5 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 bg-white/90"
            aria-label="Go back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <a
            href={platformBrand.websiteUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-2 text-xs font-semibold text-neutral-600"
          >
            Shared with {platformBrand.name}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        <main className="flex-1">
          <section className="pt-2">
            <div className="flex items-start gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-sm shadow-neutral-900/[0.04]">
                {restaurantLogo ? (
                  <img src={restaurantLogo} alt={restaurantName} className="h-full w-full object-cover" />
                ) : (
                  <Utensils className="h-7 w-7 text-neutral-400" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-neutral-500">{firstName} recommends</p>
                <h1 className="mt-1 text-[34px] font-semibold leading-[1.02] tracking-tight">
                  {restaurantName}
                </h1>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2 text-sm text-neutral-600">
              {rating ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-3 py-1.5 font-semibold text-neutral-900">
                  <Star className="h-4 w-4 fill-current" style={{ color: accentColor }} />
                  {rating.toFixed(0)}.0
                </span>
              ) : null}
              {restaurant?.address ? (
                <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-white/80 px-3 py-1.5">
                  <MapPin className="h-4 w-4 shrink-0 text-neutral-400" />
                  <span className="truncate">{restaurant.address}</span>
                </span>
              ) : null}
            </div>

            <div className="mt-7 rounded-[24px] bg-white p-5 shadow-sm shadow-neutral-900/[0.04]">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-neutral-400">Recommendation</p>
              <p className="text-[24px] font-semibold leading-8 text-neutral-950">
                "{shoutoutMessage}"
              </p>
              <p className="mt-4 text-sm leading-6 text-neutral-500">
                Shared by {firstName} after ordering here.
              </p>
            </div>
          </section>

          {highlightedItem && (
            <section className="mt-6">
              <p className="mb-3 text-sm font-semibold text-neutral-950">From this visit</p>
              <button
                type="button"
                onClick={openMenu}
                className="flex w-full items-center gap-3 rounded-2xl bg-white p-3 text-left shadow-sm shadow-neutral-900/[0.03]"
              >
                {highlightedItem.image_url ? (
                  <img src={highlightedItem.image_url} alt={highlightedItem.name} className="h-[68px] w-[68px] shrink-0 rounded-xl object-cover" />
                ) : (
                  <div className="flex h-[68px] w-[68px] shrink-0 items-center justify-center rounded-xl bg-neutral-100">
                    <Utensils className="h-6 w-6 text-neutral-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-sm font-semibold text-neutral-950">{highlightedItem.name}</p>
                  <p className="mt-1 text-sm text-neutral-500">{formatCurrency(highlightedItem.price || 0)}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-neutral-400" />
              </button>
            </section>
          )}

          {popularItems.length > 0 && (
            <section className="mt-6">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-semibold text-neutral-950">Popular here</p>
                <button type="button" onClick={openMenu} className="text-xs font-semibold" style={{ color: accentColor }}>
                  See menu
                </button>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2 thin-scroll">
                {popularItems.slice(0, 4).map((item) => (
                  <button
                    type="button"
                    onClick={openMenu}
                    key={item.id}
                    className="w-[148px] shrink-0 rounded-2xl bg-white p-3 text-left shadow-sm shadow-neutral-900/[0.03]"
                  >
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="mb-3 h-24 w-full rounded-xl object-cover" />
                    ) : (
                      <div className="mb-3 flex h-24 w-full items-center justify-center rounded-xl bg-neutral-100">
                        <Utensils className="h-6 w-6 text-neutral-400" />
                      </div>
                    )}
                    <p className="line-clamp-2 min-h-[40px] text-sm font-semibold leading-5 text-neutral-950">{item.name}</p>
                    <p className="mt-1 text-xs text-neutral-500">{formatCurrency(item.price || 0)}</p>
                  </button>
                ))}
              </div>
            </section>
          )}

          <section className="mt-6 rounded-2xl bg-neutral-900 px-4 py-4 text-white">
            <p className="text-sm font-semibold">Restaurant ordering powered by {platformBrand.name}.</p>
            <p className="mt-1 text-sm leading-5 text-white/65">
              QR menus, loyalty, reservations, and simple tools for local restaurants.
            </p>
            <a
              href={platformBrand.websiteUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-white"
            >
              Learn about {platformBrand.name}
              <ExternalLink className="h-4 w-4" />
            </a>
          </section>
        </main>

        <footer className="sticky bottom-0 -mx-5 mt-8 bg-[#f5f4ef]/95 px-5 pb-5 pt-3 backdrop-blur">
          <button
            type="button"
            onClick={openMenu}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl text-[15px] font-bold text-white active:scale-[0.99]"
            style={{ backgroundColor: accentColor }}
          >
            View menu
            <ExternalLink className="h-4 w-4" />
          </button>
          {restaurant?.address && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${restaurant.name} ${restaurant.address}`)}`}
              target="_blank"
              rel="noreferrer"
              className="mt-2 flex h-11 w-full items-center justify-center rounded-xl text-sm font-semibold text-neutral-700"
            >
              Get directions
            </a>
          )}
        </footer>
      </div>
    </div>
  );
}
